/**
 * Bitfinex API Keys Service
 * Service for managing Bitfinex API credentials
 */

import { api } from './api'

export interface BitfinexApiKey {
  id: number
  exchange: string
  label: string | null
  permissions: string[] | null
  status: 'active' | 'invalid' | 'revoked'
  last_used_at: number | null
  created_at: number
  updated_at: number
}

export interface CreateBitfinexApiKeyRequest {
  label?: string
  api_key: string
  api_secret: string
}

export interface UpdateBitfinexApiKeyRequest {
  label?: string | null
  status?: 'active' | 'invalid' | 'revoked'
}

export interface ListBitfinexApiKeysResponse {
  bitfinex_api_keys: BitfinexApiKey[]
}

export interface CreateBitfinexApiKeyResponse {
  bitfinex_api_key: BitfinexApiKey
  message: string
}

export interface BitfinexApiKeyResponse {
  bitfinex_api_key: BitfinexApiKey
}

export interface DeleteBitfinexApiKeyResponse {
  success: boolean
  message: string
}

export interface BitfinexApiKeyPermission {
  key: string
  read: boolean
  write: boolean
  label: string
  required: boolean
}

export interface VerifyBitfinexApiKeyRequest {
  api_key: string
  api_secret: string
}

export interface VerifyBitfinexApiKeyResponse {
  success: boolean
  result: {
    is_valid: boolean
    has_required_permissions: boolean
    permissions: BitfinexApiKeyPermission[]
    missing_permissions: string[]
  }
}

/**
 * Bitfinex API Keys Service
 */
export const bitfinexApiKeysService = {
  /**
   * List all Bitfinex API keys for current user
   */
  async list(): Promise<BitfinexApiKey[]> {
    const response = await api.get<ListBitfinexApiKeysResponse>('/bitfinex-api-keys')
    return response.bitfinex_api_keys
  },

  /**
   * Get specific Bitfinex API key by ID
   */
  async get(id: number): Promise<BitfinexApiKey> {
    const response = await api.get<BitfinexApiKeyResponse>(`/bitfinex-api-keys/${id}`)
    return response.bitfinex_api_key
  },

  /**
   * Create new Bitfinex API key
   */
  async create(data: CreateBitfinexApiKeyRequest): Promise<CreateBitfinexApiKeyResponse> {
    return api.post<CreateBitfinexApiKeyResponse>('/bitfinex-api-keys', data)
  },

  /**
   * Update Bitfinex API key
   */
  async update(id: number, data: UpdateBitfinexApiKeyRequest): Promise<void> {
    await api.patch(`/bitfinex-api-keys/${id}`, data)
  },

  /**
   * Delete (revoke) Bitfinex API key
   */
  async delete(id: number): Promise<void> {
    await api.delete<DeleteBitfinexApiKeyResponse>(`/bitfinex-api-keys/${id}`)
  },

  /**
   * Verify Bitfinex API key permissions without storing
   */
  async verify(data: VerifyBitfinexApiKeyRequest): Promise<VerifyBitfinexApiKeyResponse> {
    return api.post<VerifyBitfinexApiKeyResponse>('/bitfinex-api-keys/verify', data)
  },

  /**
   * Verify permissions for existing Bitfinex API key by ID
   */
  async verifyExisting(id: number): Promise<VerifyBitfinexApiKeyResponse> {
    return api.post<VerifyBitfinexApiKeyResponse>(`/bitfinex-api-keys/${id}/verify`, {})
  },
}
